import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import s from "./DirectoryPage.module.css";
import SectionPicker from "./SectionPicker";
import DatePicker from "../../components/DatePicker";
import ConfirmDialog from "../../components/ConfirmDialog";
import RowKebab from "../../components/RowKebab";
import StudentsBySectionPanel from "./StudentsBySectionPanel";
import EnrollmentsPanel from "./EnrollmentsPanel";
import StudentProfileForm, {
  uploadStudentPhotoIfPending,
  fetchNextStudentNumber,
  loadStudentPrefix,
  saveStudentPrefix,
  loadStudentPrefixes,
} from "./StudentProfileForm";
import MasterStudentList from "./MasterStudentList";
import CurrentPlacementFields from "./CurrentPlacementFields";
import { canSeeMasterList, canChangePlacement } from "./StudentProfileModal/profileUtils";

/** What the enrollment PUT tells us about the invoice after a grade change. */
const INVOICE_NOTES = {
  RECOMPUTED:       "Grade level changed — this term's tuition invoice was updated to the new grade's fees.",
  HAS_PAYMENTS:     "Grade level changed, but this term's invoice already has payments so it was NOT re-priced. Review it in Tuition and click Recompute if the fees should change.",
  NO_FEE_STRUCTURE: "Grade level changed, but there is no fee structure for the new grade this school year, so the invoice was not updated.",
};

/** Shared auth-header helper for the photo-upload fetch, which needs its
 *  own non-JSON Content-Type handling. */
function authHeaders() {
  let user = null;
  try { user = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { /* ignore */ }
  return {
    "X-User-Role":  user?.role     || "",
    "X-Username":   user?.username || "",
    ...(user?.linkedEntityId != null ? { "X-Linked-Entity-Id": String(user.linkedEntityId) } : {}),
    ...(user?.linkedEntityType     ? { "X-Linked-Entity-Type": user.linkedEntityType } : {}),
  };
}

/* ---------- Tabs ---------- */
const BASE_TABS = [
  { key: "students",       label: "Students" },
  { key: "parents",        label: "Parents" },
  { key: "parent-student", label: "Parent ↔ Student" },
  { key: "enrollments",    label: "Enrollments" },
  { key: "student-sports", label: "Sports" },
];

/* ---------- External lookup endpoints (resolved on demand for ref fields) ---------- */
const LOOKUPS = {
  sections:    {
    endpoint: "/api/sections",
    label:    "name",
    deps:     ["gradeLevels"],
    labelFn:  (sec, all) => {
      const grade = (all.gradeLevels || []).find(g => g.id === sec.gradeLevelId);
      return grade ? `${grade.name} · ${sec.name}` : sec.name;
    },
  },
  gradeLevels: { endpoint: "/api/grade-levels", label: "name" },
  schoolYears: { endpoint: "/api/school-years", label: "label" },
  semesters:   {
    endpoint: "/api/semesters",
    label:    "label",
    deps:     ["schoolYears"],
    labelFn:  (sem, all) => {
      const year = (all.schoolYears || []).find(y => y.id === sem.schoolYearId);
      const base = year ? `${year.label} · ${sem.label}` : sem.label;
      return sem.active ? `${base} · ACTIVE` : base;
    },
  },
  sports:      { endpoint: "/api/sports",       label: "name" },
};

/* ---------- Field schemas ---------- */
const SCHEMA = {
  students: {
    endpoint: "/api/students",
    columns: [
      ["studentNumber", "Student #"],
      ["lastName",      "Last name"],
      ["firstName",     "First name"],
      ["sex",           "Sex"],
      ["currentSectionId", "Current section"],
      ["status",        "Status"],
    ],
    fields: [
      { key: "studentNumber", label: "Student number", type: "text", required: true, placeholder: "S-2026-0001" },
      { key: "firstName",     label: "First name",     type: "text", required: true },
      { key: "middleName",    label: "Middle name",    type: "text" },
      { key: "lastName",      label: "Last name",      type: "text", required: true },
      { key: "birthdate",     label: "Birthdate",      type: "date" },
      { key: "sex",           label: "Sex",            type: "select",
        options: [["", "—"], ["Male", "Male"], ["Female", "Female"]] },
      { key: "contact",       label: "Contact",        type: "text" },
      { key: "address",       label: "Address",        type: "text" },
      { key: "currentSectionId", label: "Current section", type: "sectionPicker" },
      { key: "status",        label: "Status",         type: "select",
        options: [["PENDING","Pending (not enrolled yet)"], ["ENROLLED","Enrolled"],
                  ["GRADUATED","Graduated"], ["INACTIVE","Inactive"]] },
    ],
    blank: { studentNumber: "", firstName: "", middleName: "", lastName: "", birthdate: "",
             sex: "", contact: "", address: "", currentSectionId: "", status: "PENDING" },
  },

  parents: {
    endpoint: "/api/parents",
    columns: [
      ["lastName",  "Last name"],
      ["firstName", "First name"],
      ["contact",   "Contact"],
      ["email",     "Email"],
      ["occupation","Occupation"],
    ],
    fields: [
      { key: "firstName",  label: "First name", type: "text", required: true },
      { key: "lastName",   label: "Last name",  type: "text", required: true },
      { key: "contact",    label: "Contact",    type: "text" },
      { key: "email",      label: "Email",      type: "text" },
      { key: "occupation", label: "Occupation", type: "text" },
      { key: "address",    label: "Address",    type: "text" },
    ],
    blank: { firstName: "", lastName: "", contact: "", email: "", occupation: "", address: "" },
  },

  "parent-student": {
    endpoint: "/api/parent-student",
    columns: [
      ["parentId",     "Parent"],
      ["studentId",    "Student"],
      ["relationship", "Relationship"],
      ["primary",      "Primary?"],
    ],
    fields: [
      { key: "parentId",     label: "Parent",  type: "selfLookup", lookup: "parents",
        labelFn: r => `${r.lastName}, ${r.firstName}`, required: true },
      { key: "studentId",    label: "Student", type: "selfLookup", lookup: "students",
        labelFn: r => `${r.lastName}, ${r.firstName} (${r.studentNumber})`, required: true },
      { key: "relationship", label: "Relationship", type: "select", required: true,
        options: [["Father","Father"], ["Mother","Mother"], ["Guardian","Guardian"], ["Other","Other"]] },
      { key: "primary",      label: "Primary contact", type: "checkbox" },
    ],
    blank: { parentId: "", studentId: "", relationship: "Father", primary: false },
  },

  enrollments: {
    endpoint: "/api/enrollments",
    columns: [
      ["studentId",    "Student"],
      ["schoolYearId", "School Year"],
      ["semesterId",   "Semester"],
      ["sectionId",    "Section"],
      ["status",       "Status"],
      ["enrolledAt",   "Enrolled at"],
    ],
    fields: [
      { key: "studentId",    label: "Student", type: "selfLookup", lookup: "students",
        labelFn: r => `${r.lastName}, ${r.firstName} (${r.studentNumber})`, required: true },
      { key: "schoolYearId", label: "School Year", type: "lookup", lookup: "schoolYears", required: true },
      { key: "semesterId",   label: "Semester",    type: "lookup", lookup: "semesters",   required: true,
        filterBy: { formField: "schoolYearId", optionField: "schoolYearId" } },
      // Grade Level narrows the section picker below — it's a UX filter,
      // not a column on the enrollment table, so it's marked transient so
      // save() doesn't include it in the POST/PUT payload.
      { key: "gradeLevelId", label: "Grade Level", type: "lookup", lookup: "gradeLevels",
        required: true, transient: true },
      { key: "sectionId",    label: "Section",     type: "sectionPicker", required: true,
        filterBy: { formField: "schoolYearId", optionField: "schoolYearId" },
        filterByExtra: [{ formField: "gradeLevelId", optionField: "gradeLevelId" }] },
      { key: "status",       label: "Status",      type: "select",
        options: [["ENROLLED","Enrolled"], ["DROPPED","Dropped"], ["COMPLETED","Completed"]] },
    ],
    blank: { studentId: "", schoolYearId: "", semesterId: "", gradeLevelId: "", sectionId: "", status: "ENROLLED" },
  },

  "student-sports": {
    endpoint: "/api/student-sports",
    columns: [
      ["studentId",    "Student"],
      ["sportId",      "Sport"],
      ["schoolYearId", "School Year"],
      ["jerseyNumber", "Jersey #"],
      ["position",     "Position"],
    ],
    fields: [
      { key: "studentId",    label: "Student", type: "selfLookup", lookup: "students",
        labelFn: r => `${r.lastName}, ${r.firstName} (${r.studentNumber})`, required: true },
      { key: "sportId",      label: "Sport", type: "lookup", lookup: "sports", required: true },
      { key: "schoolYearId", label: "School Year", type: "lookup", lookup: "schoolYears", required: true },
      { key: "jerseyNumber", label: "Jersey #", type: "text" },
      { key: "position",     label: "Position", type: "text" },
    ],
    blank: { studentId: "", sportId: "", schoolYearId: "", jerseyNumber: "", position: "" },
  },
};

/* ---------- Page ---------- */
export default function DirectoryPage() {
  /* Evaluated inside the component so the role is read after login,
     not at module-load time when sessionStorage is still empty. */
  const TABS = useMemo(
    () => canSeeMasterList()
      ? [...BASE_TABS, { key: "master-list", label: "Master List" }]
      : BASE_TABS,
    []  // role is stable for the lifetime of the page session
  );

  const [activeTab, setActiveTab] = useState(() => TABS[0].key);
  const [data, setData]           = useState({});  // { tabKey: [rows] }
  const [lookups, setLookups]     = useState({});  // { lookupName: [rows] }
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(null);
  const [error, setError]         = useState("");
  const [busy, setBusy]           = useState(false);
  // Student tab uses a two-phase save (create/update JSON, then upload
  // photo to /photo endpoint) — this holds the picked File in the meantime.
  const [studentPhotoFile, setStudentPhotoFile] = useState(null);
  // Students → Edit: the student's current-term enrollment, editable as
  // "Grade & Section". null = not enrolled anywhere (or not on that form).
  const [placement, setPlacement] = useState(null);
  // Post-save info banner (e.g. what happened to the invoice after a
  // grade change). Separate from `error` so it isn't styled as a failure.
  const [notice, setNotice] = useState("");
  // Sticky student-number prefix (create flow only). Loaded from
  // localStorage on first render so the admin's saved value wins over the
  // SY-derived default. The list drives the dropdown inside
  // StudentProfileForm; `studentPrefix` is the currently-selected one.
  const [studentPrefixes, setStudentPrefixes] = useState(() => loadStudentPrefixes([]));
  const [studentPrefix,   setStudentPrefix]   = useState(() => loadStudentPrefix([]));
  // Pending-delete state: { row, tab, desc } | null. Set by remove(),
  // cleared by cancelDelete or after a successful confirmDelete.
  const [pendingDelete, setPendingDelete] = useState(null);
  // One-shot filter handed to the Enrollments panel from a cross-page
  // deep-link (e.g. Class Offerings → "Manage in Directory"). Read once
  // on mount, applied by EnrollmentsPanel via its initialFilter prop,
  // then cleared so a manual refresh of /directory shows the unfiltered
  // default view.
  const [enrollmentsInitialFilter, setEnrollmentsInitialFilter] = useState(null);
  // Sort state per tab — { [tabKey]: { key, dir } | null }. null = use the
  // default ordering (whatever the API returns). Tri-state click cycles
  // asc → desc → cleared, mirroring StudentsBySectionPanel.
  const [sortByTab, setSortByTab] = useState({});

  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem("directory.deeplink"); }
    catch { /* sessionStorage unavailable */ }
    if (!raw) return;
    try { sessionStorage.removeItem("directory.deeplink"); } catch { /* noop */ }
    let hint;
    try { hint = JSON.parse(raw); } catch { return; }
    if (!hint || typeof hint !== "object") return;
    if (hint.tab === "enrollments") {
      setActiveTab("enrollments");
      setEnrollmentsInitialFilter({
        sectionId:    hint.sectionId    ?? null,
        schoolYearId: hint.schoolYearId ?? null,
        semesterId:   hint.semesterId   ?? null,
      });
    } else if (hint.tab === "master-list") {
      setActiveTab("master-list");
    }
  }, []);

  // Reconcile the prefix when the schoolYears lookup finally arrives — only
  // if the admin hasn't saved one of their own. Otherwise we'd clobber the
  // saved value the moment the page hydrated.
  useEffect(() => {
    if (!lookups.schoolYears) return;
    const saved = (() => {
      try { return localStorage.getItem("studentNumberPrefix"); } catch { return null; }
    })();
    const prefixes = loadStudentPrefixes(lookups.schoolYears);
    setStudentPrefixes(prefixes);
    if (!saved) setStudentPrefix(prefixes[0]);
  }, [lookups.schoolYears]);

  // Debounced refetch while the create modal is open on the Students tab
  // and the prefix changes.
  useEffect(() => {
    if (!(editing === "new" && activeTab === "students")) return;
    const t = setTimeout(async () => {
      const next = await fetchNextStudentNumber(API_BASE, studentPrefix);
      if (next) setForm(prev => prev ? { ...prev, studentNumber: next } : prev);
    }, 400);
    return () => clearTimeout(t);
  }, [studentPrefix, editing, activeTab]);

  function handleStudentPrefixChange(p) {
    setStudentPrefix(p);
    saveStudentPrefix(p);
  }

  // master-list is a read-only tab with no SCHEMA entry — guard every
  // place that dereferences schema so we don't crash on undefined.
  const schema = SCHEMA[activeTab] || null;
  const rows   = data[activeTab] || [];

  /** Sort rows by the active sort column for this tab. Resolves
   *  display values for ref/selfLookup/sectionPicker fields so admins
   *  see alphabetical-by-name (not by raw ID). `numeric: true` gives
   *  natural ordering on student numbers etc. */
  const sortedRows = useMemo(() => {
    if (!schema) return rows;  // master-list manages its own data
    const sort = sortByTab[activeTab];
    if (!sort?.key) return rows;
    const dir = sort.dir === "desc" ? -1 : 1;
    const lc  = (x, y) => String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: "base" });
    function valueFor(row) {
      const v = row[sort.key];
      if (v == null || v === "") return "";
      const field = schema.fields.find(f => f.key === sort.key);
      if (field?.type === "lookup" || field?.type === "sectionPicker") {
        const lookupName = field.type === "sectionPicker" ? "sections" : field.lookup;
        const cfg  = LOOKUPS[lookupName];
        const list = lookups[lookupName] || [];
        const ref  = list.find(r => r.id === v);
        if (!ref) return "";
        return cfg.labelFn ? cfg.labelFn(ref, lookups) : (ref[cfg.label] || "");
      }
      if (field?.type === "selfLookup") {
        const list = data[field.lookup] || [];
        const ref = list.find(r => r.id === v);
        return ref ? field.labelFn(ref) : "";
      }
      if (typeof v === "boolean") return v ? "1" : "0";
      return String(v);
    }
    return [...rows].sort((a, b) => dir * lc(valueFor(a), valueFor(b)));
  }, [rows, sortByTab, activeTab, schema, lookups, data]);

  useEffect(() => {
    // Master List is read-only and has no SCHEMA entry — load its data
    // directly and return early so we don't touch schema.fields below.
    if (activeTab === "master-list") {
      loadSelfLookup("students");
      loadLookup("sections");
      loadLookup("gradeLevels");
      loadLookup("schoolYears");
      loadLookup("semesters");
      return;
    }

    loadTab(activeTab);
    // Pre-load any external lookup or selfLookup data referenced by this tab
    schema.fields.forEach(f => {
      if (f.type === "lookup")        loadLookup(f.lookup);
      if (f.type === "selfLookup")    loadSelfLookup(f.lookup);
      if (f.type === "sectionPicker") loadLookup("sections");
    });
    // For column rendering, load lookups for any column key that's a ref
    schema.columns.forEach(([k]) => {
      const field = schema.fields.find(ff => ff.key === k);
      if (field?.type === "lookup")        loadLookup(field.lookup);
      if (field?.type === "selfLookup")    loadSelfLookup(field.lookup);
      if (field?.type === "sectionPicker") loadLookup("sections");
    });
    // Students tab filter bar needs SY + Semester dropdowns and enrollments
    // (to know which SY/semester a student has been enrolled in).
    if (activeTab === "students") {
      loadLookup("schoolYears");
      loadLookup("semesters");
      loadSelfLookup("enrollments");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  async function loadTab(tabKey) {
    try {
      const res = await fetch(`${API_BASE}${SCHEMA[tabKey].endpoint}`);
      if (!res.ok) throw new Error(`Load ${tabKey} failed`);
      const list = await res.json();
      setData(prev => ({ ...prev, [tabKey]: list }));
    } catch (e) {
      setError(e.message);
      // Mark as "loaded but empty" on the first failed fetch so the
      // students skeleton stops shimmering. The error banner is still
      // rendered, and a retry (e.g. switching tabs and back) re-loads.
      setData(prev => (prev[tabKey] === undefined ? { ...prev, [tabKey]: [] } : prev));
    }
  }

  async function loadLookup(name) {
    // Load any dependent lookups first (e.g. sections depends on gradeLevels for labels)
    (LOOKUPS[name].deps || []).forEach(d => loadLookup(d));
    if (lookups[name]) return;
    try {
      const res = await fetch(`${API_BASE}${LOOKUPS[name].endpoint}`);
      if (!res.ok) throw new Error(`Load lookup ${name} failed`);
      const list = await res.json();
      setLookups(prev => ({ ...prev, [name]: list }));
    } catch (e) { setError(e.message); }
  }

  async function loadSelfLookup(tabKey) {
    if (data[tabKey]) return;
    try {
      const res = await fetch(`${API_BASE}${SCHEMA[tabKey].endpoint}`);
      if (!res.ok) throw new Error(`Load lookup ${tabKey} failed`);
      const list = await res.json();
      setData(prev => ({ ...prev, [tabKey]: list }));
    } catch (e) { setError(e.message); }
  }

  function startCreate(prefill) {
    // Accept an optional prefill so call sites can drop the user into the
    // "+ New" modal with some fields already set — e.g. the Pending view's
    // Enroll button passes { studentId } so the admin only has to pick a
    // section and hit Save.
    setEditing("new"); setForm({ ...schema.blank, ...(prefill || {}) });
    setStudentPhotoFile(null); setError("");
    // For the students tab, the next-number fetch is owned by an effect on
    // [studentPrefix, editing, activeTab] so it re-runs as the admin edits
    // the prefix too.
  }
  function startEdit(row) {
    const base = { ...schema.blank, ...row };
    // Enrollments: gradeLevelId is a transient UI-only narrowing filter, so
    // the backend row doesn't carry it. Derive it from the selected section
    // so the filter has a starting value that matches the row.
    if (activeTab === "enrollments" && row.sectionId) {
      const sec = (lookups.sections || []).find(x => x.id === row.sectionId);
      if (sec?.gradeLevelId != null) base.gradeLevelId = sec.gradeLevelId;
    }
    setPlacement(activeTab === "students" ? placementFor(row) : null);
    setEditing(row); setForm(base);
    setStudentPhotoFile(null); setError(""); setNotice("");
  }
  function cancel() {
    setEditing(null); setForm(null); setPlacement(null);
    setStudentPhotoFile(null); setError("");
  }

  /** The student's current-term enrollment: the one matching their
   *  currentSectionId, else the most recent. The API returns enrollments
   *  newest-first, so find()/[0] both pick the latest term. */
  function placementFor(student) {
    const mine = (data.enrollments || []).filter(e => e.studentId === student.id);
    const cur  = mine.find(e => e.sectionId === student.currentSectionId) || mine[0];
    if (!cur) return null;
    const sec = (lookups.sections || []).find(x => x.id === cur.sectionId);
    return {
      enrollmentId:      cur.id,
      schoolYearId:      cur.schoolYearId,
      semesterId:        cur.semesterId,
      gradeLevelId:      sec?.gradeLevelId ?? "",
      sectionId:         cur.sectionId,
      originalSectionId: cur.sectionId,
    };
  }

  /**
   * Update `prev[changedKey]` to `nextVal`, and also clear any field whose
   * `filterBy.formField` points at `changedKey` if its current value no
   * longer belongs to the new filter (e.g. picking a new School Year nulls
   * out Semester and Section if they belonged to the old year).
   */
  function applyCascade(prev, changedKey, nextVal) {
    const next = { ...prev, [changedKey]: nextVal };
    schema.fields.forEach(ff => {
      // Collect every filter criterion this field depends on (primary
      // filterBy + any filterByExtra). Field gets cleared if ANY one of
      // them no longer matches after the change.
      const criteria = [];
      if (ff.filterBy) criteria.push(ff.filterBy);
      if (ff.filterByExtra) criteria.push(...ff.filterByExtra);
      const relevant = criteria.filter(c => c.formField === changedKey);
      if (relevant.length === 0) return;
      const currentVal = next[ff.key];
      if (!currentVal) return;
      // Which list do we check against? lookup → lookups[ff.lookup]; sectionPicker → lookups.sections.
      const list = ff.type === "lookup"
        ? (lookups[ff.lookup] || [])
        : ff.type === "sectionPicker" ? (lookups.sections || [])
        : null;
      if (!list) return;
      const picked = list.find(r => String(r.id) === String(currentVal));
      if (!picked) { next[ff.key] = ""; return; }
      for (const crit of relevant) {
        if (String(picked[crit.optionField]) !== String(nextVal)) {
          next[ff.key] = "";
          return;
        }
      }
    });
    return next;
  }

  async function save(e) {
    e.preventDefault();
    const movePlacement = activeTab === "students" && editing !== "new"
      && placement && canChangePlacement()
      && placement.sectionId !== placement.originalSectionId;
    if (movePlacement && !placement.sectionId) {
      setError("Pick a section for the new grade level, or set the grade back.");
      return;
    }
    setBusy(true); setError(""); setNotice("");
    try {
      const isNew = editing === "new";
      const url   = isNew ? `${API_BASE}${schema.endpoint}` : `${API_BASE}${schema.endpoint}/${editing.id}`;
      const payload = { ...form };
      schema.fields.forEach(f => {
        // Transient fields are UI-only (e.g. enrollments' gradeLevelId, used
        // to narrow the section picker) — drop them before the backend POST.
        if (f.transient) {
          delete payload[f.key];
          return;
        }
        if (f.type === "lookup" || f.type === "selfLookup" || f.type === "sectionPicker" || f.type === "number") {
          if (payload[f.key] === "" || payload[f.key] === undefined) payload[f.key] = null;
          else if (payload[f.key] !== null) payload[f.key] = Number(payload[f.key]);
        }
        if (f.type === "date" && payload[f.key] === "") payload[f.key] = null;
      });
      // Students carry a photoUrl that's outside the schema fields list —
      // normalise "" to null so Remove-photo actually clears the column
      // rather than storing an empty string.
      if (activeTab === "students" && payload.photoUrl === "") payload.photoUrl = null;
      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      const saved = await res.json().catch(() => null);

      // Students → Edit with a new grade/section: move the current-term
      // enrollment. Runs after the student PUT because that PUT writes the
      // form's (old) currentSectionId; the enrollment PUT then re-syncs it.
      if (movePlacement) {
        const er = await fetch(`${API_BASE}/api/enrollments/${placement.enrollmentId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sectionId: placement.sectionId }),
        });
        if (!er.ok) {
          setError(`Student details saved, but the grade/section change failed: ${
            await extractErrorMessage(er, `error ${er.status}`)}`);
          await loadTab(activeTab);
          setBusy(false);
          return;
        }
        const moved = await er.json().catch(() => null);
        if (moved?.invoiceNote) setNotice(INVOICE_NOTES[moved.invoiceNote] || "");
        await loadTab("enrollments");
      }

      // Student tab: after the row is saved we have an id, so now upload
      // any queued photo. A failed upload leaves the row intact — admin
      // can retry from Edit.
      if (activeTab === "students" && studentPhotoFile) {
        const targetId = saved?.id || (isNew ? null : editing.id);
        if (targetId) {
          try { await uploadStudentPhotoIfPending(API_BASE, authHeaders, targetId, studentPhotoFile); }
          catch (pe) {
            setError(`Saved, but photo upload failed: ${pe.message}`);
            await loadTab(activeTab);
            setBusy(false);
            return;
          }
        }
      }

      await loadTab(activeTab);
      cancel();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  function remove(row) {
    // Stage the delete instead of running it — the styled ConfirmDialog
    // (rendered at the bottom of this component) takes over from here.
    // Capture the current activeTab so a tab-switch mid-confirm can't
    // misroute the DELETE to the wrong endpoint.
    setPendingDelete({ row, tab: activeTab, desc: describeRow(activeTab, row) });
  }

  function cancelDelete() {
    if (busy) return;
    setPendingDelete(null);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    const { row, tab } = pendingDelete;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}${SCHEMA[tab].endpoint}/${row.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      await loadTab(tab);
    } catch (e) { setError(e.message); }
    finally {
      // Close the dialog on both success and failure — leaving it open
      // would visually wedge over the page-level error banner. The error
      // (if any) is shown in the card's existing .error region.
      setPendingDelete(null);
      setBusy(false);
    }
  }

  function renderCell(row, key) {
    const v = row[key];
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "✓" : "—";
    const field = schema.fields.find(f => f.key === key);
    if (field?.type === "lookup" || field?.type === "sectionPicker") {
      const lookupName = field.type === "sectionPicker" ? "sections" : field.lookup;
      const cfg  = LOOKUPS[lookupName];
      const list = lookups[lookupName] || [];
      const ref  = list.find(r => r.id === v);
      if (!ref) return `#${v}`;
      return cfg.labelFn ? cfg.labelFn(ref, lookups) : ref[cfg.label];
    }
    if (field?.type === "selfLookup") {
      const list = data[field.lookup] || [];
      const ref = list.find(r => r.id === v);
      return ref ? field.labelFn(ref) : `#${v}`;
    }
    if (key === "enrolledAt") return new Date(v).toLocaleString();
    return String(v);
  }

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <div className={s.eyebrow}>Admin · Directory</div>
          <h1 className={s.title}>People &amp; Enrollment</h1>
          <p className={s.sub}>Students, parents, parent–student links and per-semester enrollments.</p>
        </div>
      </div>

      <div className={s.tabBar}>
        {TABS.map(t => (
          <button
            key={t.key}
            className={`${s.tab} ${activeTab === t.key ? s.tabActive : ""}`}
            onClick={() => { setActiveTab(t.key); cancel(); setNotice(""); }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className={s.card}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>{TABS.find(t => t.key === activeTab).label}</h2>
          {/* Students and Enrollments panels own their own + New buttons
              (inline in their filter bars), so suppress the card-head one there.
              Master List is read-only — no create button at all. */}
          {!editing && activeTab !== "students" && activeTab !== "enrollments" && activeTab !== "master-list" && (
            <button className={s.btnPrimary} onClick={() => startCreate()}>+ New</button>
          )}
        </div>

        {error && <div className={s.error}>{error}</div>}
        {notice && <div className={s.notice}>{notice}</div>}

        {/* ── Master List tab ──────────────────────────────────────────
            Read-only comprehensive view — has its own modal, no CRUD here.
            Receives the already-loaded students + lookup arrays so it
            doesn't fire duplicate fetches. */}
        {activeTab === "master-list" ? (
          <MasterStudentList
            students={data.students || []}
            sections={lookups.sections || []}
            gradeLevels={lookups.gradeLevels || []}
            schoolYears={lookups.schoolYears || []}
            semesters={lookups.semesters || []}
            loading={data.students === undefined}
          />
        ) : activeTab === "students" ? (
          <StudentsBySectionPanel
            students={data.students || []}
            sections={lookups.sections || []}
            gradeLevels={lookups.gradeLevels || []}
            schoolYears={lookups.schoolYears || []}
            semesters={lookups.semesters || []}
            enrollments={data.enrollments || []}
            loading={data.students === undefined}
            onEdit={startEdit}
            onDelete={remove}
            onAfterCreate={() => loadTab("students")}
          />
        ) : activeTab === "enrollments" ? (
          <EnrollmentsPanel
            enrollments={data.enrollments || []}
            schoolYears={lookups.schoolYears || []}
            semesters={lookups.semesters || []}
            students={data.students || []}
            sections={lookups.sections || []}
            gradeLevels={lookups.gradeLevels || []}
            columns={SCHEMA.enrollments.columns}
            renderCell={renderCell}
            onNew={startCreate}
            onEdit={startEdit}
            onDelete={remove}
            pageStyles={s}
            initialFilter={enrollmentsInitialFilter}
          />
        ) : (
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  {schema.columns.map(([k, label]) => (
                    <SortHeader
                      key={k}
                      label={label}
                      sortKey={k}
                      activeKey={sortByTab[activeTab]?.key || null}
                      dir={sortByTab[activeTab]?.dir || "asc"}
                      onClick={(key) => {
                        const cur = sortByTab[activeTab];
                        let next;
                        if (!cur || cur.key !== key) next = { key, dir: "asc" };
                        else if (cur.dir === "asc")  next = { key, dir: "desc" };
                        else                          next = null;
                        setSortByTab({ ...sortByTab, [activeTab]: next });
                      }}
                      styles={s}
                    />
                  ))}
                  <th className={s.actionsCol}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data[activeTab] === undefined ? (
                  /* Skeleton rows while the first fetch resolves. */
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={`sk-${i}`} className={s.skelRow}>
                      {schema.columns.map(([k]) => (
                        <td key={k}><span className={s.skel} /></td>
                      ))}
                      <td className={s.actionsCol}>
                        <span className={`${s.skel} ${s.skelActions}`} />
                      </td>
                    </tr>
                  ))
                ) : sortedRows.length === 0 ? (
                  <tr><td colSpan={schema.columns.length + 1} className={s.emptyRow}>No records yet.</td></tr>
                ) : sortedRows.map(row => (
                  <tr key={row.id}>
                    {schema.columns.map(([k]) => <td key={k}>{renderCell(row, k)}</td>)}
                    <td className={s.actionsCol}>
                      <div className={s.rowActions}>
                        <button className={s.btnGhost} onClick={() => startEdit(row)}>Edit</button>
                        <RowKebab
                          items={[
                            { label: "Delete", danger: true, onClick: () => remove(row) },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Create / Edit modal ─────────────────────────────────────────
          Click-outside does NOT dismiss (would lose form data); close via
          Cancel or ×. Same pattern as the Academics tab.

          Portaled to <body> so position:fixed centers on the viewport
          rather than on .shell (which has a transform: translateY that
          creates a fixed-positioning containing block). Without this,
          scrolling a long list before clicking Edit pushes the modal
          off-screen. */}
      {editing && createPortal(
        <div className={s.formOverlay}>
          <form onSubmit={save} className={s.formModal}>
            <div className={s.formModalHead}>
              <h3 className={s.formModalTitle}>
                {editing === "new" ? "New " : "Edit "}
                {singularLabel(TABS.find(t => t.key === activeTab)?.label || "")}
              </h3>
              <button type="button" className={s.closeBtn}
                      onClick={cancel} disabled={busy} aria-label="Close">✕</button>
            </div>

            {error && <div className={s.error}>{error}</div>}

            <div className={s.formModalBody}>
              {activeTab === "students" ? (
                /* Profile-style form for students: photo + full field set.
                   The raw currentSectionId picker stays hidden — it would
                   only change the cached column. Grade/section changes go
                   through CurrentPlacementFields below, which edits the
                   enrollment (EnrollmentController keeps the cache in sync). */
                <StudentProfileForm
                  value={form}
                  onChange={next => setForm(next)}
                  sections={lookups.sections || []}
                  gradeLevels={lookups.gradeLevels || []}
                  photoFile={studentPhotoFile}
                  onPhotoFileChange={setStudentPhotoFile}
                  showSectionPicker={false}
                  showStatus={editing !== "new"}
                  disabled={busy}
                  /* Prefix field only makes sense when creating — editing an
                     existing student shouldn't offer to change the prefix. */
                  prefix={editing === "new" ? studentPrefix : undefined}
                  onPrefixChange={editing === "new" ? handleStudentPrefixChange : undefined}
                  prefixes={editing === "new" ? studentPrefixes : undefined}
                />
              ) : null}
              {activeTab === "students" && editing !== "new" && (
                <CurrentPlacementFields
                  placement={placement}
                  onChange={setPlacement}
                  sections={lookups.sections || []}
                  gradeLevels={lookups.gradeLevels || []}
                  schoolYears={lookups.schoolYears || []}
                  semesters={lookups.semesters || []}
                  editable={canChangePlacement()}
                  disabled={busy}
                />
              )}
              {activeTab !== "students" && (
                <div className={s.form}>
                  {schema.fields.map(f => {
                    // Cascading filter: narrow a lookup's / sectionPicker's options
                    // based on another form field's value (e.g. Semester options are
                    // limited to those that belong to the chosen School Year).
                    let filteredLookupData = null;
                    if (f.type === "lookup") {
                      const fullList = lookups[f.lookup] || [];
                      if (f.filterBy) {
                        const filterVal = form[f.filterBy.formField];
                        // No parent value yet → no options (forces user to pick SY first).
                        filteredLookupData = filterVal
                          ? fullList.filter(r => String(r[f.filterBy.optionField]) === String(filterVal))
                          : [];
                      } else {
                        filteredLookupData = fullList;
                      }
                    } else if (f.type === "selfLookup") {
                      filteredLookupData = data[f.lookup];
                    }
                    // For sectionPicker, build a filtered sections snapshot
                    // so it only shows sections in the chosen school year.
                    // filterBy = required primary filter (empty list until the
                    // parent is picked). filterByExtra = optional narrowers
                    // (e.g. grade level) that further trim the list.
                    let filteredAllLookups = lookups;
                    if (f.type === "sectionPicker" && (f.filterBy || f.filterByExtra)) {
                      let sectionsList = lookups.sections || [];
                      if (f.filterBy) {
                        const filterVal = form[f.filterBy.formField];
                        sectionsList = filterVal
                          ? sectionsList.filter(r => String(r[f.filterBy.optionField]) === String(filterVal))
                          : [];
                      }
                      for (const crit of (f.filterByExtra || [])) {
                        const v = form[crit.formField];
                        if (v) {
                          sectionsList = sectionsList.filter(r => String(r[crit.optionField]) === String(v));
                        }
                      }
                      filteredAllLookups = { ...lookups, sections: sectionsList };
                    }
                    return (
                      <FormField
                        key={f.key}
                        field={f}
                        value={form[f.key]}
                        onChange={v => setForm(prev => applyCascade(prev, f.key, v))}
                        lookupData={filteredLookupData}
                        allLookups={filteredAllLookups}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <div className={s.formModalFoot}>
              <button type="button" className={s.btnGhost} onClick={cancel} disabled={busy}>Cancel</button>
              <button type="submit" className={s.btnPrimary} disabled={busy}>
                {busy ? "Saving..." : (editing === "new" ? "Create" : "Save changes")}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={`Delete ${singularLabel(TABS.find(t => t.key === (pendingDelete?.tab))?.label || "")}?`}
        message="This action cannot be undone."
        description={pendingDelete?.desc || null}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        busy={busy}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

/** Turn a tab label into a singular modal title ("Parents" → "Parent",
 *  "Sports" → "Sport", "Parent ↔ Student" → "Parent ↔ Student link"). */
/** Sortable column header. Renders a button inside the th so keyboard
 *  + screen-reader users can sort, and exposes aria-sort with the
 *  current direction. Tri-state click cycles asc → desc → cleared. */
function SortHeader({ label, sortKey, activeKey, dir, onClick, styles }) {
  const isActive = activeKey === sortKey;
  const ariaSort = !isActive ? "none" : (dir === "desc" ? "descending" : "ascending");
  return (
    <th aria-sort={ariaSort} className={styles.thSortable}>
      <button
        type="button"
        className={`${styles.sortBtn} ${isActive ? styles.sortBtnActive : ""}`}
        data-dir={isActive ? dir : "none"}
        onClick={() => onClick(sortKey)}>
        <span>{label}</span>
        <span className={styles.sortArrow} aria-hidden="true" />
      </button>
    </th>
  );
}

function singularLabel(tabLabel) {
  if (!tabLabel) return "record";
  if (tabLabel === "Parent ↔ Student") return "Parent ↔ Student link";
  if (tabLabel.endsWith("ies")) return tabLabel.slice(0, -3) + "y";
  if (tabLabel.endsWith("s"))   return tabLabel.slice(0, -1);
  return tabLabel;
}

/** Build a human-readable description of a row for the delete-confirm
 *  dialog so the admin can see exactly which record they're about to
 *  remove. Returns null when we don't have a richer label, in which case
 *  the dialog falls back to its generic copy. */
function describeRow(tabKey, row) {
  if (!row) return null;
  switch (tabKey) {
    case "students": {
      const name = [row.lastName, row.firstName].filter(Boolean).join(", ");
      return name ? (row.studentNumber ? `${name} · ${row.studentNumber}` : name) : null;
    }
    case "parents": {
      const name = [row.lastName, row.firstName].filter(Boolean).join(", ");
      return name || null;
    }
    default:
      return null;
  }
}

/* ---------- Form field ---------- */
function FormField({ field, value, onChange, lookupData, allLookups }) {
  if (field.type === "sectionPicker") {
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <SectionPicker
          value={value ? Number(value) : null}
          onChange={id => onChange(id == null ? "" : id)}
          sections={(allLookups && allLookups.sections) || []}
          gradeLevels={(allLookups && allLookups.gradeLevels) || []}
          required={field.required}
        />
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className={s.checkboxRow}>
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
        <span>{field.label}</span>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <select className={s.input} value={value ?? ""} onChange={e => onChange(e.target.value)} required={field.required}>
          {field.options.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "lookup") {
    const list = lookupData || [];
    const cfg = LOOKUPS[field.lookup];
    const optLabel = r => cfg.labelFn ? cfg.labelFn(r, allLookups || {}) : (r[cfg.label] || `#${r.id}`);
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <select className={s.input} value={value ?? ""} onChange={e => onChange(e.target.value)} required={field.required}>
          <option value="">— select —</option>
          {list.map(r => <option key={r.id} value={r.id}>{optLabel(r)}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "selfLookup") {
    const list = lookupData || [];
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <select className={s.input} value={value ?? ""} onChange={e => onChange(e.target.value)} required={field.required}>
          <option value="">— select —</option>
          {list.map(r => <option key={r.id} value={r.id}>{field.labelFn(r)}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "date") {
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <DatePicker
          value={value || ""}
          onChange={v => onChange(v)}
          placeholder={field.placeholder || "MM/DD/YYYY"}
        />
      </div>
    );
  }
  return (
    <div className={s.field}>
      <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
      <input
        className={s.input}
        type={field.type}
        value={value ?? ""}
        placeholder={field.placeholder || ""}
        onChange={e => onChange(e.target.value)}
        required={field.required}
      />
    </div>
  );
}
